import { useCallback, useEffect, useRef } from "react";
import Axios from "axios";
import { useAtom } from "jotai";
import { environment } from "../../environment";
import { useLogin } from "../login";
import {
    CAPABILITIES_FALLBACK,
    ConnectState,
    ICapabilities,
    ShopifyHealthState,
    capabilitiesAtom,
    capabilitiesLoadingAtom,
} from "./types";

// Module-level de-dupe: many components call useCapabilities() on the same paint,
// but only one pair of network probes should ever be in the air.
let inFlight: Promise<ICapabilities> | null = null;
// Guards the window-focus refetch from firing on every tab flip.
const FOCUS_REFRESH_COOLDOWN_MS = 30000;
let lastResolvedAt = 0;

// #region resolveConnectState
/**
 * Derives the four-state Connect status. Prefers the backend's own `state`
 * field (task 03) and only falls back to the legacy boolean triple.
 */
const resolveConnectState = (payload: any): ConnectState => {
    const provided = payload?.state;
    if (provided === 'not_started' || provided === 'incomplete' || provided === 'complete' || provided === 'restricted') {
        return provided;
    }
    if (!payload) return 'not_started';
    if (payload.disabled_reason) return 'restricted';
    if (payload.payouts_enabled && payload.charges_enabled) return 'complete';
    if (payload.details_submitted) return 'incomplete';
    return 'not_started';
};

// #region fetchCapabilities
const fetchCapabilities = async (authToken: string | null, brandId: number | null): Promise<ICapabilities> => {
    const headers = { 'X-Wallmates-Auth': authToken };

    // Fail closed on both probes: better to hide a Shopify screen than to render
    // one for a brand that has no store. `known` records whether the probe actually
    // answered — a 401/network failure must not be reported as "not connected", or
    // every connected brand gets told to connect.
    const shopifyProbe = Axios.get(`${environment.brandChannelUrl}/verify-shopify-connection`, { headers })
        .then((res) => ({
            connected: res.data?.connected === true,
            plus: res.data?.shopify_plus === true,
            // Live health of the store, separate from whether we hold credentials.
            // 'ok' | 'store_unavailable' | 'unauthorized' | 'unknown'.
            state: (res.data?.state ?? null) as ShopifyHealthState,
            reason: (res.data?.reason ?? null) as string | null,
            publicationsScopeGranted: typeof res.data?.publicationsScopeGranted === 'boolean'
                ? res.data.publicationsScopeGranted as boolean
                : null,
            known: true
        }))
        .catch(() => ({ connected: false, plus: false, state: null as ShopifyHealthState, reason: null, publicationsScopeGranted: null as boolean | null, known: false }));

    const payoutProbe = brandId
        ? Axios.get(`${environment.payoutUrl}/status?brand_id=${brandId}`, { headers })
            .then((res) => res.data)
            .catch(() => null)
        : Promise.resolve(null);

    // Lets a brand with internal/API orders but no storefront past the Shopify gate.
    // Fails closed to false: a brand that genuinely has no orders still gets the
    // "connect a store" guidance rather than an empty table.
    const orderSourceProbe = Axios.get(`${environment.ordersUrl}/order-sources`, { headers })
        .then((res) => res.data?.has_non_shopify_orders === true)
        .catch(() => false);

    const [shopify, payout, hasNonShopifyOrders] = await Promise.all([shopifyProbe, payoutProbe, orderSourceProbe]);
    const connectState = resolveConnectState(payout);

    return {
        hasShopify: shopify.connected,
        shopifyKnown: shopify.known,
        hasNonShopifyOrders,
        canLicense: true,
        connectState,
        connectOnboardingComplete: connectState === 'complete',
        connectDisabledReason: payout?.disabled_reason ?? null,
        connectCurrentlyDue: payout?.currently_due ?? payout?.requirements?.currently_due ?? [],
        colorVariantsAllowed: shopify.plus,
        shopifyHealth: shopify.state,
        shopifyHealthReason: shopify.reason,
        publicationsScopeGranted: shopify.publicationsScopeGranted,
    };
};

// #region useCapabilities
/**
 * One shared capability hook. Reads from a Jotai atom so the sidebar, the wizard
 * and every gated page agree without each running its own probes.
 *
 * `capabilities === null` means "not resolved yet" — consumers must render a
 * skeleton rather than the ungated content.
 */
export const useCapabilities = () => {
    const [capabilities, setCapabilities] = useAtom(capabilitiesAtom);
    const [loading, setLoading] = useAtom(capabilitiesLoadingAtom);

    const [user, authToken] = useLogin();
    const brandId = user?.brand_id ?? null;

    const brandIdRef = useRef(brandId);
    const tokenRef = useRef(authToken);
    useEffect(() => { brandIdRef.current = brandId; tokenRef.current = authToken; }, [brandId, authToken]);

    const run = useCallback(async (): Promise<ICapabilities> => {
        if (inFlight) return inFlight;
        setLoading(true);
        inFlight = fetchCapabilities(tokenRef.current, brandIdRef.current)
            .catch(() => CAPABILITIES_FALLBACK)
            .finally(() => { inFlight = null; lastResolvedAt = Date.now(); });

        const resolved = await inFlight;
        setCapabilities(resolved);
        setLoading(false);
        return resolved;
    }, [setCapabilities, setLoading]);

    // Populate once when nothing has resolved yet. A cached result whose Shopify
    // probe never answered is not a real answer — retry it rather than leaving the
    // whole shell stuck on the fail-closed fallback.
    const load = useCallback(async (): Promise<ICapabilities | null> => {
        if (inFlight) return capabilities;
        if (capabilities && capabilities.shopifyKnown) return capabilities;
        if (!tokenRef.current) return null;
        return run();
    }, [capabilities, run]);

    // Force a refetch — used after a Shopify connect or a Connect onboarding return
    // so the whole shell re-renders without a re-login.
    const refresh = useCallback(async (): Promise<ICapabilities | null> => {
        if (!tokenRef.current) return null;
        return run();
    }, [run]);

    return {
        capabilities,
        hasShopify: capabilities?.hasShopify ?? false,
        shopifyKnown: capabilities?.shopifyKnown ?? false,
        hasNonShopifyOrders: capabilities?.hasNonShopifyOrders ?? false,
        canLicense: capabilities?.canLicense ?? true,
        connectState: capabilities?.connectState ?? 'not_started',
        connectOnboardingComplete: capabilities?.connectOnboardingComplete ?? false,
        connectDisabledReason: capabilities?.connectDisabledReason ?? null,
        connectCurrentlyDue: capabilities?.connectCurrentlyDue ?? [],
        colorVariantsAllowed: capabilities?.colorVariantsAllowed ?? false,
        // Null until the probe answers. Only 'ok' means the store is actually usable;
        // `hasShopify` stays true for a dark store so its settings page remains reachable.
        shopifyHealth: capabilities?.shopifyHealth ?? null,
        shopifyHealthReason: capabilities?.shopifyHealthReason ?? null,
        // False only when Shopify confirmed the publications permissions are missing.
        publicationsScopeGranted: capabilities?.publicationsScopeGranted ?? null,
        // Distinguishes "still resolving" from "resolved to false". A cached result
        // whose Shopify probe errored out counts as unresolved: consumers keep
        // showing a skeleton instead of telling a connected brand to connect.
        resolved: capabilities !== null && capabilities.shopifyKnown,
        loading,
        load,
        refresh,
    };
};

// #region useCapabilitiesReady
/**
 * Mount-once bootstrapper. Call from the dashboard shell; every other consumer
 * just reads the atom via useCapabilities().
 */
export const useCapabilitiesReady = () => {
    const capabilityFunctions = useCapabilities();
    const { load, refresh, shopifyKnown } = capabilityFunctions;

    useEffect(() => {
        load();
    }, [load]);

    const knownRef = useRef(shopifyKnown);
    useEffect(() => { knownRef.current = shopifyKnown; }, [shopifyKnown]);

    // Re-derive on tab focus so a connect finished in another tab lands here too.
    useEffect(() => {
        const onFocus = () => {
            if (inFlight) return;
            // An unanswered Shopify probe skips the cooldown — the app is otherwise
            // stuck on the fail-closed fallback until the next full reload.
            if (knownRef.current && Date.now() - lastResolvedAt < FOCUS_REFRESH_COOLDOWN_MS) return;
            refresh();
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [refresh]);

    return capabilityFunctions;
};
